import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientsApi, ClientsListParams, CreateClientData, UpdateClientData } from '../services/clients';

// Query keys
export const clientKeys = {
  all: ['clients'] as const,
  lists: () => [...clientKeys.all, 'list'] as const,
  list: (params: ClientsListParams) => [...clientKeys.lists(), params] as const,
  details: () => [...clientKeys.all, 'detail'] as const,
  detail: (id: string) => [...clientKeys.details(), id] as const,
  stats: () => [...clientKeys.all, 'stats'] as const,
  tags: () => [...clientKeys.all, 'tags'] as const,
};

// List clients
export function useClients(params: ClientsListParams = {}) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientsApi.list(params),
  });
}

// Get single client
export function useClient(id: string) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: () => clientsApi.get(id),
    enabled: !!id,
  });
}

// Get client stats
export function useClientStats() {
  return useQuery({
    queryKey: clientKeys.stats(),
    queryFn: () => clientsApi.getStats(),
  });
}

// Get all tags
export function useClientTags() {
  return useQuery({
    queryKey: clientKeys.tags(),
    queryFn: () => clientsApi.getTags(),
  });
}

// Create client
export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateClientData) => clientsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.tags() });
    },
  });
}

// Update client
export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientData }) =>
      clientsApi.update(id, data),
    onSuccess: (client) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.setQueryData(clientKeys.detail(client._id), client);
    },
  });
}

// Delete client
export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, permanent = false }: { id: string; permanent?: boolean }) =>
      clientsApi.delete(id, permanent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.stats() });
    },
  });
}

// Add note
export function useAddClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, content }: { clientId: string; content: string }) =>
      clientsApi.addNote(clientId, content),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
    },
  });
}

// Delete note
export function useDeleteClientNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ clientId, noteId }: { clientId: string; noteId: string }) =>
      clientsApi.deleteNote(clientId, noteId),
    onSuccess: (_, { clientId }) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(clientId) });
    },
  });
}

// Update tags
export function useUpdateClientTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, tags }: { id: string; tags: string[] }) =>
      clientsApi.updateTags(id, tags),
    onSuccess: (client) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: clientKeys.tags() });
      queryClient.setQueryData(clientKeys.detail(client._id), client);
    },
  });
}
